import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";

const VerificationCodeModal = ({
  isOpen,
  handleClose,
  onConfirm,
  onResend,
  isLoading,
  error,
  errorMessage,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const { t } = useTranslation();

  const handleInputChange = (e) => {
    const { value } = e.target;
    setVerificationCode(value);
  };

  const handleSubmit = () => {
    onConfirm(verificationCode);
  };

  const handleResendClick = () => {
    onResend();
    setResendCountdown(30); // Set countdown for 30 seconds
  };

  // Countdown effect
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendCountdown]);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <Box sx={{ padding: '16px' }}>
        <DialogTitle sx={{ position: 'relative', padding: '10px 16px 0', marginBottom: '20px' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px', textAlign: 'center' }}>
            {t("Authentication")}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              color: 'rgba(0, 0, 0, 0.54)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '16px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 300, color: '#475467', marginBottom: '10px', textAlign: 'center' }}>
            {t("Please enter the authentication code sent to your email to continue")}
          </Typography>

          <Typography sx={{ marginBottom: '0px', fontWeight: 500, fontSize: '14px', color: '#000' }}>
            {t("Authentication Code")}
          </Typography>

          <TextField
            value={verificationCode}
            onChange={handleInputChange}
            placeholder="xxxxxx"
            fullWidth
            error={error}
            helperText={error && errorMessage}
            inputProps={{
              style: { fontSize: '14px', marginTop: 0 },
            }}
            variant="outlined"
          />

          {/* Resend Code Button with Countdown */}
          <Button
            onClick={handleResendClick}
            disabled={resendCountdown > 0}  // Disable button if countdown is active
            sx={{
              textTransform: 'none',
              marginTop: '8px',
              fontWeight: 500,
              fontSize: '14px',
              color: resendCountdown > 0 ? 'gray' : '#2F37B5', // Change color when disabled
              alignSelf: 'flex-start'
            }}
          >
            {resendCountdown > 0 ? `${t("Resend Code")} in ${resendCountdown}s` : t("Resend Code")}
          </Button>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', display: 'flex', padding: '8px 16px' }}>
          <Box sx={{ flex: 1, mr: 1 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              fullWidth
              sx={{
                height: '48px',
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                {t('Cancel')}
              </Typography>
            </Button>
          </Box>
          <Box sx={{ flex: 1, ml: 1 }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                background: '#2F37B5',
                borderRadius: '8px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? (
              <>
                <CircularProgress size={24} sx={{ color: "#fff", marginRight: "5px" }} />
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
                  {t("Verifying")}...
                </Typography>
              </>
              ) : (
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
                  {t("Confirm")}
                </Typography>
              )}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

VerificationCodeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default VerificationCodeModal;